import { useState, useEffect } from 'preact/hooks';
import { fetchWeek, fetchStravaWeek } from '../api';
import { getWeekEnd } from '../utils';
import type { WeekState, DayData, StravaActivity } from '../types';

const EMPTY_STATE: WeekState = {
  days: [],
  prevDays: [],
  activities: [],
  calError: false,
  stravaError: null,
};

// Simple cache to make back-navigation instant
const cache = new Map<number, WeekState>();

export function useWeekData(offset: number) {
  const [state, setState] = useState<WeekState>(() => cache.get(offset) || EMPTY_STATE);
  const [loading, setLoading] = useState(!cache.has(offset));

  useEffect(() => {
    // Return cached data immediately if available
    if (cache.has(offset)) {
      setState(cache.get(offset)!);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    async function load() {
      const end = getWeekEnd(offset);
      const prevEnd = (() => {
        const d = new Date(end + 'T00:00:00');
        d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 10);
      })();

      // Parallel fetch -- all three requests at once
      const [daysResult, prevDaysResult, stravaResult] = await Promise.allSettled([
        fetchWeek(end),
        fetchWeek(prevEnd),
        fetchStravaWeek(end),
      ]);

      if (cancelled) return;

      const days: DayData[] = daysResult.status === 'fulfilled' ? daysResult.value : [];
      const calError = daysResult.status === 'rejected';

      const prevDays: DayData[] = prevDaysResult.status === 'fulfilled' ? prevDaysResult.value : [];

      let activities: StravaActivity[] = [];
      let stravaError: string | null = null;

      if (stravaResult.status === 'fulfilled') {
        const data = stravaResult.value;
        if (Array.isArray(data)) {
          activities = data;
        } else if ('error' in data && data.error === 'not_authorized') {
          stravaError = data.authUrl || null;
        } else {
          stravaError = 'STRAVA OFFLINE';
        }
      } else {
        const err = stravaResult.reason;
        stravaError = err instanceof Error && err.name === 'AbortError'
          ? 'STRAVA TIMEOUT'
          : 'STRAVA OFFLINE';
      }

      const newState: WeekState = { days, prevDays, activities, calError, stravaError };
      cache.set(offset, newState);
      setState(newState);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [offset]);

  return { ...state, loading };
}
